import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from './tooltip';

interface TruncatedTooltipProps {
  content: string;
  className?: string;
  /** When set, clamps to this many lines instead of a single line. */
  maxLines?: number;
  /** Preserve newlines in both the trigger and the tooltip content (e.g. multiline addresses). */
  preserveWhitespace?: boolean;
}

export function TruncatedTooltip({
  content,
  className,
  maxLines,
  preserveWhitespace = false,
}: TruncatedTooltipProps) {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const checkTruncation = () => {
    if (textRef.current) {
      if (maxLines) {
        // Multi-line clamp: truncated when content overflows the clamped height
        setIsTruncated(textRef.current.scrollHeight > textRef.current.offsetHeight);
      } else {
        // Single-line truncate: truncated when content overflows the width
        setIsTruncated(textRef.current.scrollWidth > textRef.current.offsetWidth);
      }
    }
  };

  useEffect(() => {
    checkTruncation();
    // Re-check on window resize to handle responsive layouts
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [content]);

  const lineClampMap: Record<number, string> = {
    1: 'line-clamp-1',
    2: 'line-clamp-2',
    3: 'line-clamp-3',
    4: 'line-clamp-4',
    5: 'line-clamp-5',
  };
  const lineClampClass = maxLines ? (lineClampMap[maxLines] ?? 'line-clamp-2') : 'truncate';

  return (
    <Tooltip open={isTruncated ? undefined : false}>
      <TooltipTrigger asChild>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          ref={textRef}
          onMouseEnter={checkTruncation}
          className={cn(
            'block w-full',
            lineClampClass,
            preserveWhitespace && 'whitespace-pre-line',
            className
          )}
        >
          {content}
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>
          <p className={cn('max-w-md break-words', preserveWhitespace && 'whitespace-pre-line')}>
            {content}
          </p>
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}
